export const AccountablePlanStatuses = {
    UNKNOWN: 'unknown',
    IN_PROGRESS: 'in_progress',
    SKIPPED: 'skipped',
    SUBMITTED: 'submitted',
} as const;

type AccountablePlanStatus = (typeof AccountablePlanStatuses)[keyof typeof AccountablePlanStatuses];

export interface AccountablePlanData {
    uuid: string;
    member: number;
    year: number;
    month: number;
    is_renting: boolean;
    square_footage_home: string;
    square_footage_office: string;
    total_reimbursements: string;
    cost_security: string;
    cost_cleaning: string;
    cost_utilities: string;
    cost_HOA: string;
    cost_home_insurance: string;
    cost_property_taxes: string;
    cost_mortgage_interest: string;
    cost_rent: string;
    cost_rent_insurance: string;
    miles_driven_total: string;
    miles_driven_business: string;
    internet_phone_business_percent: string;
    cost_internet: string;
    cost_phone: string;
    created_at: string;
    updated_at: string;
    status: AccountablePlanStatus;
    // This period's home address as it read when the plan was filed — a stored string, not a
    // relation, so it cannot follow the member on a move. Rendered by the same `Address.one_line`
    // the member profile reads live, so the two strings cannot drift.
    //
    // Empty rather than null when there is no snapshot: the column is `TextField(blank=True,
    // default="")`, so a plan filed before it existed, or by a member with no address on file,
    // serializes as `''`. The key is absent only against a backend that predates the field.
    // Both cases mean "fall back to the live profile address" — see `useAccountablePlanAddress`,
    // which is why it tests for emptiness rather than nullishness.
    //
    // Read-only on the serializer, so it round-trips harmlessly through `undoCastPlanValues`
    // and the PUT.
    one_line_home_address?: string;
}

export interface UseAccountablePlansParams {
    year?: number;
    member?: number | `${number}`;
}

export type UseAccountablePlansResponse = AccountablePlanData[];

export interface ParsedAccountablePlanData {
    uuid: string;
    member: number;
    year: number;
    month: number;
    is_renting: boolean;
    square_footage_home: number;
    square_footage_office: number;
    total_reimbursements: number;
    cost_security: number;
    cost_cleaning: number;
    cost_utilities: number;
    cost_HOA: number;
    cost_home_insurance: number;
    cost_property_taxes: number;
    cost_mortgage_interest: number;
    cost_rent: number;
    cost_rent_insurance: number;
    miles_driven_total: number;
    miles_driven_business: number;
    internet_phone_business_percent: number;
    cost_internet: number;
    cost_phone: number;
    created_at: string;
    updated_at: string;
    status: AccountablePlanStatus;
    mileage_reimbursement_total?: number;
    mileage_rate?: number;
    home_office_reimbursement_total?: number;
    phone_internet_reimbursement_total?: number;
    out_of_pocket_expenses?: number;
    // See `AccountablePlanData` above — not touched by `castPlanValues`.
    one_line_home_address?: string;
}
